import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats, getEmails, getApprovals } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useNotifications } from '../../lib/notifications';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatDate, getStatusColor, truncate } from '../../lib/utils';
import {
  Mail,
  MailOpen,
  Clock,
  CheckCircle,
  Users,
  FileText,
  AlertCircle,
  ArrowRight,
  Bell,
  TrendingUp,
  BellRing,
} from 'lucide-react';

export const Dashboard = () => {
  const { user, isManager } = useAuth();
  const { permission, requestPermission } = useNotifications();
  const [stats, setStats] = useState(null);
  const [recentEmails, setRecentEmails] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, emailsRes, approvalsRes] = await Promise.all([
        getDashboardStats(),
        getEmails(),
        getApprovals('pending'),
      ]);
      setStats(statsRes.data);
      setRecentEmails(emailsRes.data.slice(0, 5));
      setPendingApprovals(approvalsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'رسائل جديدة',
      titleEn: 'New Emails',
      value: stats?.new_emails || 0,
      icon: Mail,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'قيد المعالجة',
      titleEn: 'In Progress',
      value: stats?.in_progress || 0,
      icon: Clock,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
    },
    {
      title: 'بانتظار الموافقة',
      titleEn: 'Pending Approval',
      value: stats?.pending_approvals || 0,
      icon: AlertCircle,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
    },
    {
      title: 'عروض الأسعار النشطة',
      titleEn: 'Active Quotes',
      value: stats?.active_quotes || 0,
      icon: FileText,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'الموردين',
      titleEn: 'Suppliers',
      value: stats?.total_suppliers || 0,
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            مرحباً، {user?.name}
          </h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {permission !== 'granted' && (
            <Button
              onClick={requestPermission}
              variant="outline"
              size="sm"
              className="rounded-sm border-border"
              data-testid="enable-notifications-btn"
            >
              <BellRing className="h-4 w-4 mr-2" />
              تفعيل الإشعارات
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm border-border"
            data-testid="notifications-btn"
          >
            <Bell className="h-4 w-4 mr-2" />
            <span className="font-mono text-xs">
              {stats?.new_emails || 0}
            </span>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className="bg-card border-border rounded-sm hover:border-primary/40 transition-all duration-300"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-sm ${stat.bgColor}`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <span className="font-mono text-2xl font-bold">{stat.value}</span>
              </div>
              <div className="mt-3">
                <p className="text-sm font-medium">{stat.title}</p>
                <p className="text-xs text-muted-foreground">{stat.titleEn}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Emails */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-xl uppercase tracking-tight">
              آخر الرسائل
            </CardTitle>
            <Link to="/emails">
              <Button variant="ghost" size="sm" className="text-primary" data-testid="view-all-emails-btn">
                عرض الكل
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentEmails.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MailOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>لا توجد رسائل</p>
              </div>
            ) : (
              recentEmails.map((email) => (
                <Link
                  key={email.id}
                  to={`/emails/${email.id}`}
                  className="block p-3 rounded-sm bg-muted/30 border border-border hover:border-primary/40 transition-all"
                  data-testid={`email-item-${email.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium truncate">
                          {email.sender_name || email.sender}
                        </span>
                      </div>
                      <p className="text-sm mt-1 truncate">{email.subject}</p>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {truncate(email.body, 60)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge
                        variant="outline"
                        className={`text-xs rounded-sm ${getStatusColor(email.status)}`}
                      >
                        {email.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">
                        {formatDate(email.created_at)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {/* Pending Approvals (Manager Only) */}
        {isManager() && (
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-xl uppercase tracking-tight">
                طلبات الموافقة
              </CardTitle>
              <Link to="/approvals">
                <Button variant="ghost" size="sm" className="text-primary" data-testid="view-all-approvals-btn">
                  عرض الكل
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>لا توجد طلبات معلقة</p>
                </div>
              ) : (
                pendingApprovals.map((approval) => (
                  <Link
                    key={approval.id}
                    to={`/approvals/${approval.id}`}
                    className="block p-3 rounded-sm bg-muted/30 border border-border hover:border-primary/40 transition-all"
                    data-testid={`approval-item-${approval.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                          <span className="font-medium truncate">
                            {approval.title}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          من: {approval.requested_by_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {truncate(approval.content_preview, 50)}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className="text-xs rounded-sm bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
                      >
                        {approval.type}
                      </Badge>
                    </div>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions (Non-Manager) */}
        {!isManager() && (
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase tracking-tight">
                إجراءات سريعة
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Link to="/emails">
                <Button
                  variant="outline"
                  className="w-full h-24 flex-col gap-2 rounded-sm border-border hover:border-primary/40"
                  data-testid="quick-emails-btn"
                >
                  <Mail className="h-6 w-6" />
                  <span>صندوق الوارد</span>
                </Button>
              </Link>
              <Link to="/suppliers">
                <Button
                  variant="outline"
                  className="w-full h-24 flex-col gap-2 rounded-sm border-border hover:border-primary/40"
                  data-testid="quick-suppliers-btn"
                >
                  <Users className="h-6 w-6" />
                  <span>الموردين</span>
                </Button>
              </Link>
              <Link to="/quotes">
                <Button
                  variant="outline"
                  className="w-full h-24 flex-col gap-2 rounded-sm border-border hover:border-primary/40"
                  data-testid="quick-quotes-btn"
                >
                  <FileText className="h-6 w-6" />
                  <span>عروض الأسعار</span>
                </Button>
              </Link>
              <Link to="/chat">
                <Button
                  variant="outline"
                  className="w-full h-24 flex-col gap-2 rounded-sm border-border hover:border-primary/40"
                  data-testid="quick-chat-btn"
                >
                  <TrendingUp className="h-6 w-6" />
                  <span>المساعد الذكي</span>
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
